import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { toast } from "sonner";
import BottomTabNav from "../../components/BottomTabNav";
import { Mic, Volume2, Heart, ArrowRight, Sparkles, Star, Phone } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

// Dialect options with flags
const dialectOptions = [
  { code: "NEUTRAL", label: "Neutral Spanish", flag: "🌎" },
  { code: "DO", label: "Dominican Spanish", flag: "🇩🇴" },
  { code: "MX", label: "Mexican Spanish", flag: "🇲🇽" },
  { code: "ES", label: "Spain Spanish", flag: "🇪🇸" },
  { code: "CO", label: "Colombian Spanish", flag: "🇨🇴" },
  { code: "AR", label: "Argentine Spanish", flag: "🇦🇷" }
];

const scenarioTitles = {
  emergency: "Emergency Help 🚨",
  food: "Ordering Food 🍽️",
  date: "Going on a Date 💕",
  directions: "Need Directions 🧭",
  hotel: "Hotel / Travel Help 🏨",
  general: "General Conversation 💬"
};

const quickPhrases = {
  emergency: [
    "I need a doctor urgently!",
    "Call an ambulance!",
    "I'm having chest pain",
    "Where is the hospital?",
    "I need help now!",
    "I lost my passport",
    "I've been robbed",
    "I need the police",
    "I'm allergic to...",
    "I need my medication",
    "Someone is injured",
    "This is an emergency"
  ],
  food: [
    "I would like the menu please",
    "What do you recommend?",
    "I'm vegetarian",
    "I'm allergic to nuts",
    "Can I have the check?",
    "Is this gluten-free?",
    "I'd like to order this",
    "Can I have water please?",
    "This looks delicious",
    "Do you have vegan options?",
    "Table for two, please",
    "What's in this dish?"
  ],
  date: [
    "You look beautiful tonight",
    "I'm having a great time",
    "Tell me about yourself",
    "What do you like to do?",
    "This place is amazing",
    "Can I see you again?",
    "I really enjoyed this",
    "You make me smile",
    "I'd love to know you better",
    "Where are you from?",
    "What's your favorite...",
    "Shall we go somewhere else?"
  ],
  directions: [
    "How do I get to...?",
    "Where is the train station?",
    "Can you take me to...?",
    "How much to go there?",
    "Is this the right way?",
    "How far is it?",
    "Can you show me on the map?",
    "I'm lost",
    "Which bus goes to...?",
    "Is it walking distance?",
    "Turn left/right here",
    "Stop here please"
  ],
  hotel: [
    "I have a reservation",
    "What time is check-in?",
    "Can I see the room first?",
    "The WiFi isn't working",
    "I need extra towels",
    "What time is breakfast?",
    "Can you call a taxi?",
    "I'd like a wake-up call",
    "Is there a gym?",
    "Can you recommend a restaurant?",
    "I need to extend my stay",
    "The AC isn't working"
  ],
  general: [
    "Hello, how are you?",
    "Thank you very much",
    "Can you help me?",
    "I don't understand",
    "Please speak slowly",
    "How much does this cost?",
    "Where is the bathroom?",
    "Do you speak English?",
    "What time is it?",
    "Excuse me",
    "I'm sorry",
    "Have a nice day"
  ]
};

const TranslatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [text, setText] = useState("");
  const [translated, setTranslated] = useState("");
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [scenario, setScenario] = useState(location.state?.scenario || "general");
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [playingPhraseIndex, setPlayingPhraseIndex] = useState(null);
  const [targetGender, setTargetGender] = useState(() => {
    return localStorage.getItem('passportTalk_targetGender') || 'neutral';
  });
  const [dialect, setDialect] = useState(() => {
    return localStorage.getItem('passportTalk_dialect') || 'NEUTRAL';
  });

  useEffect(() => {
    if (location.state?.scenario) {
      setScenario(location.state.scenario);
    }
  }, [location.state]);

  useEffect(() => {
    localStorage.setItem('passportTalk_targetGender', targetGender);
  }, [targetGender]);

  useEffect(() => {
    localStorage.setItem('passportTalk_dialect', dialect);
  }, [dialect]);

  const handleTranslate = async (textToTranslate = text) => {
    if (!textToTranslate || !String(textToTranslate).trim()) {
      toast.error("Please enter text to translate");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/translate', {
        text: textToTranslate,
        source_lang: "en",
        target_lang: "es",
        mode: "travel",
        scenario: scenario,
        target_person_gender: targetGender,
        dialect_country: dialect
      });
      setTranslated(response.data.translated_text);
      toast.success("Translation complete!");
    } catch (error) {
      console.error("Translation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Translation failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePhraseClick = async (phrase) => {
    setText(phrase);
    if (autoTranslate) {
      await handleTranslate(phrase);
    }
  };

  const handleSavePhrase = async (phrase) => {
    try {
      // First translate the phrase
      const response = await api.post('/translate', {
        text: phrase,
        source_lang: "en",
        target_lang: "es",
        mode: "travel",
        scenario: scenario,
        target_person_gender: targetGender,
        dialect_country: dialect
      });
      
      // Then save to favorites
      await api.post('/favorites', {
        source_text: phrase,
        translated_text: response.data.translated_text,
        mode: "travel",
        scenario: scenario,
        dialect_country: dialect,
        target_person_gender: targetGender
      });
      toast.success("Phrase saved to favorites!");
    } catch (error) {
      toast.error("Failed to save phrase");
    }
  };

  const handleSpeak = () => {
    if (!translated) return;
    const utterance = new SpeechSynthesisUtterance(translated);
    utterance.lang = "es-ES";
    if (scenario === "emergency") {
      utterance.rate = 0.8; // Slower for emergency
    }
    window.speechSynthesis.speak(utterance);
  };

  const handlePhraseAudio = async (phrase, index) => {
    try {
      // Stop any currently playing speech
      window.speechSynthesis.cancel();
      
      // First check if we already have the translation in state
      let translatedPhrase = translated;
      
      // If the phrase matches what's in the input, use existing translation
      if (text !== phrase) {
        // Need to translate this phrase first
        const response = await api.post('/translate', {
          text: phrase,
          source_lang: "en",
          target_lang: "es",
          mode: "travel",
          scenario: scenario,
          target_person_gender: targetGender,
          dialect_country: dialect
        });
        translatedPhrase = response.data.translated_text;
      }
      
      // Play the audio
      if (translatedPhrase) {
        setPlayingPhraseIndex(index);
        
        const utterance = new SpeechSynthesisUtterance(translatedPhrase);
        utterance.lang = "es-ES";
        if (scenario === "emergency") {
          utterance.rate = 0.8; // Slower and clearer for emergency
        }
        
        // Clear playing state when audio ends
        utterance.onend = () => {
          setPlayingPhraseIndex(null);
        };
        
        // Also clear on error
        utterance.onerror = () => {
          setPlayingPhraseIndex(null);
        };
        
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      setPlayingPhraseIndex(null);
      toast.error("Failed to play audio");
    }
  };

  const handleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window)) {
      toast.error("Voice input not supported");
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setText(transcript);
    };

    recognition.start();
  };

  const handleSaveFavorite = async () => {
    if (!text || !translated) {
      toast.error("Nothing to save");
      return;
    }

    try {
      await api.post('/favorites', {
        source_text: text,
        translated_text: translated,
        mode: "travel",
        scenario: scenario,
        dialect_country: dialect,
        target_person_gender: targetGender
      });
      toast.success("Saved to favorites!");
    } catch (error) {
      toast.error("Failed to save favorite");
    }
  };

  return (
    <div className="min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-2xl mx-auto">
        {/* Scenario badge */}
        {scenario !== "general" && (
          <div className="mb-4 flex items-center gap-2">
            <div className="bg-gradient-to-r from-[#0EA5E9] to-[#06b6d4] text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              {scenarioTitles[scenario]}
            </div>
          </div>
        )}

        <h1 className="text-4xl font-bold text-travel-navy font-outfit mb-2" data-testid="translate-page-title">Translate</h1>
        <p className="text-slate-600 mb-4">English → Spanish</p>

        {/* Gender-aware translation control */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Speaking to:</span>
            <div className="flex rounded-full bg-slate-100 p-1" data-testid="gender-selector">
              {[
                { value: 'woman', label: 'Woman', icon: '👩' },
                { value: 'man', label: 'Man', icon: '👨' },
                { value: 'neutral', label: 'Neutral', icon: '👤' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTargetGender(option.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                    targetGender === option.value
                      ? 'bg-white text-travel-navy shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`gender-${option.value}`}
                >
                  <span>{option.icon}</span>
                  <span className="hidden sm:inline">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Helps with gender-specific words in Spanish translations
          </p>
        </div>

        {/* Dialect selector */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-slate-700">Dialect:</span>
            <select
              value={dialect}
              onChange={(e) => setDialect(e.target.value)}
              className="bg-slate-100 border-0 rounded-full px-4 py-2 text-sm font-medium text-travel-navy focus:ring-2 focus:ring-[#0EA5E9]/20 cursor-pointer"
              data-testid="dialect-selector"
            >
              {dialectOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.flag} {option.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-slate-500">
            Choose a dialect for region-specific translations
          </p>
        </div>

        {/* Emergency CTA */}
        {scenario === "emergency" && (
          <button
            onClick={() => navigate("/live-help")}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-2xl p-4 mb-4 font-bold shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
            data-testid="emergency-live-help-button"
          >
            <Phone className="w-5 h-5" />
            Connect to Live Help Now
          </button>
        )}

        {/* Quick Phrases Section */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-travel-navy">
              {scenario === "emergency" ? "Emergency Phrases" : "Quick Phrases"}
            </h3>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={autoTranslate}
                onChange={(e) => setAutoTranslate(e.target.checked)}
                className="w-4 h-4 text-[#0EA5E9] border-slate-300 rounded focus:ring-2 focus:ring-[#0EA5E9]/20"
                data-testid="auto-translate-toggle"
              />
              <span className="font-medium">Auto-translate</span>
            </label>
          </div>
          
          <div className="flex flex-wrap gap-2" data-testid="quick-phrases-container">
            {quickPhrases[scenario].map((phrase, index) => {
              const isPlaying = playingPhraseIndex === index;
              return (
                <div
                  key={index}
                  className="group relative"
                >
                  <button
                    onClick={() => handlePhraseClick(phrase)}
                    className={`border-2 text-slate-700 px-4 py-2 rounded-full text-sm font-medium transition-all active:scale-95 pr-20 ${
                      isPlaying
                        ? "bg-[#0EA5E9]/20 border-[#0EA5E9] text-travel-navy animate-pulse"
                        : "bg-slate-50 hover:bg-[#0EA5E9]/10 border-slate-200 hover:border-[#0EA5E9] hover:text-travel-navy"
                    }`}
                    data-testid={`phrase-${index}`}
                  >
                    {phrase}
                  </button>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePhraseAudio(phrase, index);
                      }}
                      className={`w-6 h-6 flex items-center justify-center transition-colors ${
                        isPlaying
                          ? "text-[#0EA5E9] animate-pulse"
                          : "text-slate-400 hover:text-[#0EA5E9]"
                      }`}
                      data-testid={`audio-phrase-${index}`}
                      title={isPlaying ? "Playing..." : "Play audio"}
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSavePhrase(phrase);
                      }}
                      className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-yellow-500 transition-colors"
                      data-testid={`save-phrase-${index}`}
                      title="Save to favorites"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-2">English</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type or speak..."
            className="w-full h-32 bg-slate-50 border-2 border-slate-200 focus:border-[#0EA5E9] focus:ring-4 focus:ring-[#0EA5E9]/10 rounded-xl p-4 resize-none transition-all"
            data-testid="translate-input"
          />
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleVoiceInput}
              className={`flex-1 h-12 rounded-full font-semibold transition-all ${
                isListening
                  ? "bg-red-500 text-white"
                  : "bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50"
              } flex items-center justify-center gap-2`}
              data-testid="voice-input-button"
            >
              <Mic className="w-5 h-5" />
              {isListening ? "Listening..." : "Speak"}
            </button>
            <button
              onClick={() => handleTranslate()}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-[#0EA5E9] to-[#06b6d4] hover:from-[#0284c7] hover:to-[#0891b2] text-white rounded-full h-12 font-bold shadow-lg shadow-[#0EA5E9]/30 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="translate-button"
            >
              {loading ? "Translating..." : "Translate"}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {translated && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-4" data-testid="translation-result">
            <label className="block text-sm font-semibold text-slate-700 mb-2">Spanish</label>
            <div className="bg-sage/30 border border-[#99f6e4] rounded-xl p-4 mb-4">
              <p className="text-travel-navy text-lg">{translated}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSpeak}
                className="flex-1 bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full h-12 font-semibold transition-all flex items-center justify-center gap-2"
                data-testid="speak-button"
              >
                <Volume2 className="w-5 h-5" />
                Play Audio
              </button>
              <button
                onClick={handleSaveFavorite}
                className="flex-1 bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full h-12 font-semibold transition-all flex items-center justify-center gap-2"
                data-testid="save-favorite-button"
              >
                <Heart className="w-5 h-5" />
                Save
              </button>
            </div>
          </div>
        )}

        {/* Live help banner */}
        <button
          onClick={() => navigate("/live-help")}
          className="w-full bg-gradient-to-r from-sage/40 to-[#CCFBF1]/60 hover:from-sage/60 hover:to-[#CCFBF1]/80 border-2 border-[#99f6e4] text-travel-navy rounded-2xl p-6 transition-all shadow-sm hover:shadow-md"
          data-testid="need-live-help-button"
        >
          <p className="font-bold text-lg mb-1">Need live help?</p>
          <p className="text-sm text-slate-600">Connect with a real interpreter instantly</p>
        </button>
      </div>

      <BottomTabNav />
    </div>
  );
};

export default TranslatePage;
